import tinycolor from 'tinycolor2';
import { GRAY_SCALE } from 'constants/ThemeConstant';
import { getSideNavThemeBg } from './themeMeta';

/**
 * Nền cột module-menu trái:
 * - Theme không có ảnh sidebar (light, dark, liberation, …) → màu phẳng moduleMenuBg
 * - Theme theo mùa có sideNavBg → gradient từ moduleMenuBg
 */
export const getModuleMenuRailBackground = (themeKey, moduleMenuBg) =>
    getSideNavThemeBg(themeKey)
        ? generateLeftMenuGradient(moduleMenuBg)
        : moduleMenuBg;

/**
 * Sinh gradient cho menu từ màu gốc
 * @param {string} color - màu hex (#1677ff)
 * @returns {string} CSS linear-gradient
 */
export const generateLeftMenuGradient = (color) => {
    const base = tinycolor(color);

    return `linear-gradient(
        180deg,
        ${base.clone().lighten(8).toHexString()} 0%,
        ${base.clone().toHexString()} 45%,
        ${base.clone().darken(10).toHexString()} 100%
    )`;
};
export const generateRightMenuGradient = (color) => {
    const base = tinycolor(color);

    return `linear-gradient(
        180deg,
        ${base.clone().darken(10).setAlpha(0.18).toRgbString()} 0%,
        ${base.clone().setAlpha(0.08).toRgbString()} 50%,
        ${base.clone().lighten(2).setAlpha(0.05).toRgbString()} 100%
    )`;
};
export function rgba(hex, opacity = 1) {
    if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        throw new Error('Invalid Hex');
    }

    const decimal = parseInt(hex.substring(1), 16);

    const r = (decimal >> 16) & 255;
    const g = (decimal >> 8) & 255;
    const b = decimal & 255;

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const toOpaqueColor = (color) => tinycolor(color).setAlpha(1).toRgbString();

/** Pha màu foreground (alpha) lên nền solid — dùng cho table fixed column hover/selected */
export const mixOnBackground = (foreground, alpha, background = '#ffffff') =>
    tinycolor.mix(foreground, background, (1 - alpha) * 100).toHexString();

export const TABLE_STRIPED_DARK_BG = '#f5f5f5';

/** Màu opaque cho hover/selected table row — tránh rgba trong suốt khiến nội dung scroll xuyên qua cột fixed */
export const createOpaqueTableRowColors = (primary, lightBg = '#ffffff', stripedDarkBg = TABLE_STRIPED_DARK_BG) => ({
    hover: mixOnBackground(primary, 0.08, lightBg),
    selected: mixOnBackground(primary, 0.15, lightBg),
    selectedHover: mixOnBackground(primary, 0.24, lightBg),
    darkHover: mixOnBackground(primary, 0.08, stripedDarkBg),
    darkSelected: mixOnBackground(primary, 0.15, stripedDarkBg),
    darkSelectedHover: mixOnBackground(primary, 0.24, stripedDarkBg),
});

export const getTableRowTokens = ({ hover, selected, selectedHover }) => ({
    rowHoverBg: hover,
    rowSelectedBg: selected,
    rowSelectedHoverBg: selectedHover,
});

/**
 * Token header Table trên nền colorPrimary đặc.
 * Hover/active: pha trắng nhẹ lên primary (cùng tông, không trôi gần trắng như 0.15 primary trên white).
 */
export const getColoredHeaderTableTokens = (primary) => ({
    headerSplitColor: rgba(
        tinycolor(GRAY_SCALE.WHITE).toHexString(),
        0.35
    ),
    // ~12% / ~22% trắng trên primary → sáng hơn header nhưng vẫn cùng màu chủ đạo
    headerSortHoverBg: mixOnBackground(GRAY_SCALE.WHITE, 0.12, primary),
    headerSortActiveBg: mixOnBackground(GRAY_SCALE.WHITE, 0.22, primary),
    headerIconColor: GRAY_SCALE.WHITE,
    headerIconHoverColor: GRAY_SCALE.WHITE,
});

/** Icon sort: chưa active trắng mờ; .active trắng rõ để nhận chiều tăng/giảm */
export const buildThemeTableSorterScopedStyles = (primary) => `
.ant-table-wrapper .ant-table-thead .ant-table-column-sorter,
.ant-table-wrapper .ant-table-thead .ant-table-column-sorter-up:not(.active),
.ant-table-wrapper .ant-table-thead .ant-table-column-sorter-down:not(.active) {
    color: ${rgba(tinycolor(GRAY_SCALE.WHITE).toHexString(), 0.45)} !important;
}
.ant-table-wrapper .ant-table-thead .ant-table-column-sorter-up.active,
.ant-table-wrapper .ant-table-thead .ant-table-column-sorter-down.active {
    color: ${GRAY_SCALE.WHITE} !important;
}
`;

export const buildOpaqueTableRowScopedStyles = ({
    hover,
    selected,
    selectedHover,
    darkHover,
    darkSelected,
    darkSelectedHover,
}) => `
body[data-theme='light'] .ant-table-row.table-row-light:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-light > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-light:hover td:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark:hover td:not(.nagaco-comment-cell) {
    background: ${hover} !important;
}
body[data-theme='light'] .ant-table-row.table-row-dark:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark:hover td:not(.nagaco-comment-cell) {
    background: ${darkHover} !important;
}
body[data-theme='light'] .ant-table-row.table-row-light.ant-table-row-selected > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-light.ant-table-row-selected > td:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark.ant-table-row-selected > td:not(.nagaco-comment-cell) {
    background: ${selected} !important;
}
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark.ant-table-row-selected > td:not(.nagaco-comment-cell) {
    background: ${darkSelected} !important;
}
body[data-theme='light'] .ant-table-row.table-row-light.ant-table-row-selected:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-light.ant-table-row-selected > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-light.ant-table-row-selected:hover td:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark.ant-table-row-selected:hover td:not(.nagaco-comment-cell) {
    background: ${selectedHover} !important;
}
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected:hover > .ant-table-cell:not(.nagaco-comment-cell),
body[data-theme='light'] .ant-table-row.table-row-dark.ant-table-row-selected > .ant-table-cell-row-hover:not(.nagaco-comment-cell),
body[data-theme='light'] .table-row-dark.ant-table-row-selected:hover td:not(.nagaco-comment-cell) {
    background: ${darkSelectedHover} !important;
}
body[data-theme='light'] .ant-table-wrapper .ant-table-tbody .ant-table-cell-fix-left,
body[data-theme='light'] .ant-table-wrapper .ant-table-tbody .ant-table-cell-fix-right {
    z-index: 3 !important;
}
body[data-theme='light'] .ant-table-wrapper .ant-table-thead .ant-table-cell-fix-left,
body[data-theme='light'] .ant-table-wrapper .ant-table-thead .ant-table-cell-fix-right {
    z-index: 4 !important;
}
`;

export const THEME_CHECKBOX_BORDER_RADIUS = 4;

/** Checkbox — góc 4px, không dùng border-radius token chung (22px) */
export const buildThemeCheckboxScopedStyles = (cssThemeMode = 'light') => `
body[data-theme='${cssThemeMode}'] .ant-checkbox,
body[data-theme='${cssThemeMode}'] .ant-checkbox .ant-checkbox-inner {
    border-radius: ${THEME_CHECKBOX_BORDER_RADIUS}px !important;
}
`;

const normalizeAccentColor = (accentColor) =>
    typeof accentColor === 'string' && accentColor.startsWith('rgba')
        ? tinycolor(accentColor).toHexString()
        : accentColor;

/** Bảng màu group row — nền accent (amber/gold) + chữ trắng; icon expand dùng primary */
export const createThemeGroupRowPalette = (primary, accentColor = '#FFC466', surfaceBg = '#ffffff') => {
    const base = tinycolor(primary);
    const accent = tinycolor(normalizeAccentColor(accentColor));
    const onLightSurface = !tinycolor(surfaceBg).isDark();
    const white = GRAY_SCALE.WHITE;
    const primaryHex = base.toHexString();
    const accentHex = accent.toHexString();

    if (onLightSurface) {
        // Dùng đúng accent từng theme (vd. nationalDay #FFC466) — không spin/saturate lệch màu
        const groupBorder = accent.clone().darken(6).toHexString();

        const depth = (lightenAmount) => ({
            bg: accent.clone().lighten(lightenAmount).toHexString(),
            border: groupBorder,
            text: white,
        });

        return {
            depth0: depth(0),
            depth1: depth(3),
            depth2: depth(6),
            depth3: depth(9),
            accent: accentHex,
            expandBg: primaryHex,
            expandColor: white,
            myGroupRowBg: accentHex,
            cellBorder: groupBorder,
        };
    }

    const depth = (mixAlpha) => {
        const bg = mixOnBackground(primary, mixAlpha, surfaceBg);
        const border = mixOnBackground(primary, Math.min(mixAlpha + 0.06, 0.35), surfaceBg);
        return {
            bg,
            border,
            text: accentHex,
        };
    };

    return {
        depth0: depth(0.24),
        depth1: depth(0.18),
        depth2: depth(0.12),
        depth3: depth(0.08),
        accent: accentHex,
        expandBg: primaryHex,
        expandColor: white,
        myGroupRowBg: mixOnBackground(primary, 0.2, surfaceBg),
        cellBorder: mixOnBackground(primary, 0.28, surfaceBg),
    };
};

/** CSS variables cho table groupby — inject qua scoped styles của từng theme */
export const buildThemeGroupRowCssVariables = (
    primary,
    accentColor,
    cssThemeMode = 'light',
    surfaceBg = '#ffffff',
) => {
    const { depth0, depth1, depth2, depth3, accent, myGroupRowBg, cellBorder, expandBg, expandColor } =
        createThemeGroupRowPalette(primary, accentColor, surfaceBg);

    return `
body[data-theme='${cssThemeMode}'] {
    --nagaco-group-row-bg-0: ${depth0.bg};
    --nagaco-group-row-text-0: ${depth0.text};
    --nagaco-group-row-border-0: ${depth0.border};
    --nagaco-group-row-bg-1: ${depth1.bg};
    --nagaco-group-row-text-1: ${depth1.text};
    --nagaco-group-row-border-1: ${depth1.border};
    --nagaco-group-row-bg-2: ${depth2.bg};
    --nagaco-group-row-text-2: ${depth2.text};
    --nagaco-group-row-border-2: ${depth2.border};
    --nagaco-group-row-bg-3: ${depth3.bg};
    --nagaco-group-row-text-3: ${depth3.text};
    --nagaco-group-row-border-3: ${depth3.border};
    --nagaco-group-accent: ${accent};
    --nagaco-my-group-row-bg: ${myGroupRowBg};
    --nagaco-group-cell-border: ${cellBorder};
    --nagaco-group-expand-bg: ${expandBg};
    --nagaco-group-expand-color: ${expandColor};
}
`;
};

/** Token Pagination — trang active nền primary, chữ trắng, bo tròn */
export const getThemePaginationTokens = (primaryColor, borderColor) => ({
    itemActiveBg: primaryColor,
    colorPrimary: GRAY_SCALE.WHITE,
    colorPrimaryHover: GRAY_SCALE.WHITE,
    itemBg: 'transparent',
    itemLinkBg: GRAY_SCALE.WHITE,
    itemInputBg: GRAY_SCALE.WHITE,
    colorBgContainer: GRAY_SCALE.WHITE,
    colorBorder: borderColor,
    borderRadius: '50%',
});

/** Scoped CSS Pagination — viền accent, trang khác chữ xám */
export const buildThemePaginationScopedStyles = (accentGold, borderColor) => `
.ant-pagination .ant-pagination-item:not(.ant-pagination-item-active) a {
    color: ${GRAY_SCALE.GRAY};
}
.ant-pagination .ant-pagination-item-active {
    border-color: ${accentGold} !important;
    box-shadow: 0 0 0 1px ${rgba('#FFD700', 0.4)};
}
.ant-pagination .ant-pagination-prev .ant-pagination-item-link,
.ant-pagination .ant-pagination-next .ant-pagination-item-link {
    color: ${GRAY_SCALE.GRAY_LIGHT};
    background-color: ${GRAY_SCALE.WHITE};
    border-color: ${borderColor};
}
.ant-pagination .ant-pagination-options .ant-select .ant-select-selector {
    border-radius: 22px !important;
}
`;

/** Scoped CSS Segmented — tab active: nền primary, chữ + icon trắng */
export const buildThemeSegmentedScopedStyles = (
    selectedBg,
    selectedTextColor = GRAY_SCALE.WHITE
) => `
.ant-segmented .ant-segmented-thumb {
    background: ${selectedBg} !important;
    box-shadow: none !important;
}

.ant-segmented .ant-segmented-item-selected {
    background: ${selectedBg} !important;
    box-shadow: none !important;
}

.ant-segmented .ant-segmented-item-selected,
.ant-segmented .ant-segmented-item-selected .ant-segmented-item-label,
.ant-segmented .ant-segmented-item-selected .ant-segmented-item-icon,
.ant-segmented .ant-segmented-item-selected .anticon {
    color: ${selectedTextColor} !important;
}
`;
